/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {AxiosError, CancelToken} from 'axios';
import {Moment} from 'moment';
import {UserContext} from '../services/MicroServices';
import SmartSearchPaging from '../services/SmartSearchPaging';
import {DateType} from '../utils/DateUtil';
import {
  Account,
  AEMPConfig,
  AlertNotification,
  Application,
  Asset,
  AssetType,
  AuditRecord,
  BaseSearchFilters,
  CTCEntity,
  CVSAssetTypes,
  Device,
  DeviceConfiguration,
  DeviceConfigurations,
  Devices,
  DeviceTypes,
  DownloadJob,
  Entity,
  EntityStatus,
  GenericObject,
  GeoFenceAttributes,
  Group,
  HVSRecord,
  InventoryDevice,
  InventoryDevices,
  IOProfile,
  JobStatus,
  Link,
  Links,
  MaintenanceSchedule,
  MaintenanceServiceItem,
  Notification,
  NotificationType,
  Operator,
  PegBehavior,
  PegBehaviors,
  PegLabel,
  PegLabels,
  PegLabelType,
  Role,
  Route,
  Schedule,
  Subtask,
  SubtaskError,
  SubtaskErrors,
  User
} from './entities';
import {DateRange} from './general';
/* eslint-disable no-multi-spaces */

//defines service modules
export enum Module {
  MaintenanceModule = 'maintenance',
  ArcGISModule      = 'arcgis',
  AlertsModule      = 'alerts'
};

// defines the supported CTC Service endpoints which can be invoked ...
export enum Service {
  Base = 'ctcURL',
  Connect = 'serviceURL',
  PULS = 'pulsBaseUrl',
  Results = 'resultsServiceURL',
  Alerts = 'alertsServiceURL',
  iOnVision = 'ionVisionServiceURL',
  iOnMicroServices = 'microServiceURL',
  Maintenance = 'maintenanceServiceURL',
  Audit = 'auditServiceURL',
  Reports = 'reportsServiceURL',
  Crashboxx = 'crashboxxServiceURL',
  SensorsService = 'sensorsServiceURL',
  PulsBaseUrl = 'pulsBaseUrl',
  ApiBaseUrl = 'apiBaseUrl',
  CommonServiceUrl = 'commonServiceURL',
  CarrierServiceUrl = 'carrierServiceURL',
  VehicleServiceUrl = 'vehicleServiceURL'
};

// Defines the default max number of results to be returned.
export const MAX_RESULTS = 1000;

export enum ArcGISService {
  Landmarks = 'Landmarks',
  CVS = 'CVS_V2'
};

export enum ArcGISServer {
  MapServer = 'MapServer',
  FeatureServer = 'FeatureServer'
};

// defines the valid ctc sort direction tokens ...
export enum SortDirection {
  Ascending  = '+',
  Descending = '-'
};

// defines the valid ctc sort direction tokens ...
export enum FilterTypes {
  Equals  = 'equals',
  NotEqual  = 'notEqual',
  Contains  = 'contains',
  NotContains = 'notContains',
  StartsWith  = 'startsWith',
  EndsWith  = 'endsWith',
  InRange = 'inRange',
  GreaterThan = 'greaterThan',
  LessThan = 'lessThan',
  GreaterThanOrEqual = 'greaterThanOrEqual',
  LessThanOrEqual = 'lessThanOrEqual',
  Set = 'set'
};

export enum SearchType {
  asset    = 'asset',
  vehicle  = 'vehicle',
  geofence = 'geofence',
  driver   = 'driver',
  place    = 'place',
  trailer  = 'Trailer',
  scdevice = 'scdevice',
  fcdevice = 'fcdevice'
};

export interface LoadParams {
  accountId?: number;
  pageId?: number;
  pageSize?: number;
  exportLimit?: number;
  includeSubGroups?: boolean;
  includeAccountFilter?: boolean;
  systemDefined?: boolean;
  queryString?: any;
  deleted?: boolean;
  isExtensions?: boolean;
  usePulse?: boolean;
  applicationKeys?: string;
  isCtcAdmin?: boolean;
  sort?: string;
};

export enum GeoFenceOperation {
  add = 'add',
  update = 'update'
}

// defines the service error type which provides additional properties of interest ...
export interface ServiceError {
  status?: number;
  message?: string;
  errorCode: number;
  errorText: string;
  response: object;
  data: string;
  url: string;
  method: string;
  isCritical: boolean;
  isWarning: boolean;
  isServerError: boolean;
  isUnauthorizedError: boolean;
  isPermissionsError: boolean;
  isNotFoundError: boolean;
  isUnresolvableError: boolean;
  isOffline: boolean;
  is401?: boolean;
  is403?: boolean;
  is404?: boolean;
  is500?: boolean;
  error: AxiosError;
}

// defines the connect (CTC Platform) client configuration type ...
export interface ConnectConfig {
  serviceURL?: string;
  resultsServiceURL?: string;
  alertsServiceURL?: string;
  ionVisionServiceURL?: string;
  microServiceURL?: string;
  maintenanceServiceURL?: string;
  auditServiceURL?: string;
  reportsServiceURL?: string;
  appKey?: string;
  ctcVersion?: number;
  reportsServiceId?: string;
};
// defines the SDCTC connect client configuration type ...
export interface SDConnectConfig {
  serviceURL?: string;
  appKey?: string;
  apiVersion?: string;
};

// defines the ArcGIS configuration type ...
export interface ArcGISConfig {
  mapServiceURL?: string;
  cvsServiceURL?: string;
  hvsServiceURL?: string;
  endPointToken?: string;
  arcGISPortalURL?: string;
  arcGISProxyURL?: string;
  arcGISServiceURLPrefix?: string;
  arcGISServiceAPIPrefix?: string;
};

// defines the HERE configuration type ...

export interface HEREConfig {
  hereFleetServiceURL?: string;
  hereApiKey?: string;
}

// defines the base authorization header ...
export interface AuthorizationHeader {
  Authorization?: string;
}
// defines the connect (CTC Platform) request headers type ...
export interface ConnectHeaders extends AuthorizationHeader {
  'Content-Type': string;
  'calamp-services-app': string;
};

// defines the SDCTC connect client request headers type ...
export interface SDConnectHeaders extends AuthorizationHeader {
  'Content-Type': string;
  'x-app-key': string;
};

// defines the ArcGIS headers type ...
export interface ArcGISHeaders extends AuthorizationHeader {
  'Content-Type': string;
  'Accept': string;
};

// defines the ArcGIS headers type ...
export interface HereHeaders extends AuthorizationHeader {
  'Content-Type': string;
};

// defines the axios client configurations type ...
export interface AxiosConfig extends ConnectConfig, ArcGISConfig, HEREConfig {
  withCredentials: boolean
  baseURL: string;
  timeout: number;
  headers: ConnectHeaders | ArcGISHeaders | SDConnectHeaders | HereHeaders;
};

// defines the searchable properties of an iOn Vision event search ...
export type IONVisionSearchParams = {
  createdOn?: string;
  appmsgEventTime?: Date;
  avlEventTime?: string;
  groups?: string[];
  accountId?: number[];
  deviceId?: string;
  avlEventUuid?: string;
  appMessageUuid?: string;
  id?: number,
  ctcDeviceId?: string;
}

export interface SearchVisionLoadParams extends LoadParams {
  searchTerms: IONVisionSearchParams;
  sortOn?: AssetSort[];
  maxResults?: number;
}

export interface DVRUploadRequestPayload {
  serialNumber: string;
  startTimeUTC: string;
  endTimeUTC: string;
  videoEventId?: string;
  videoQuality?: number;
  videoResolution?: string;
  unitSystem?: string;
  enableTimelapse?: boolean;
  timelapseCaptureInterval?: number;
  timelapseDisplayInterval?: number;
}

export interface DVRFetchPayload {
  createdOn?: string;
  groups?: string[];
  accountId?: string;
  requestType?: string;
  avlEventUuid?: string[];
  appMessageUuid?: string[];
  avlEventTime?: string[];
  id?: number[];
  appmsgEventTime?: string[];
  deviceId?: string[];
}

export interface DVRSearchPayload {
  pageId: number;
  pageSize: number;
  search: {
    maxResults: number;
    searchTerms: DVRFetchPayload;
  }
}

export interface VideoSearchPayload extends DVRSearchPayload {};

// defines the searchable properties for accounts ...
export type AccountSearchableProperties = {
  groups?: string[];
  parent?: number;
  lastModifiedOn?: Date;
  status?: EntityStatus;
  subscription?: number;
  externalRefID?: string;
  name?: string;
  checkPulsDeviceOwnership?: boolean;
  solomonId?: string;
  id?: number
}

// Search map suggestions properties
export interface ArcGISSearchMapSuggestProps {
  text: string;
  searchExtent?: string;
  maxSuggestions?: number;
  cancelToken?: CancelToken;
}

// Search map address candidates properties
export interface ArcGISSearchMapAddressCandidatesProps {
  magicKey: string;
  singleLine: string;
}

export type GetIONVisionDetailsParams = {
  deviceEsn: string,
  start?: Date | Moment,
  end?: Date | Moment,
  records?: HVSRecord[],
  maxResults?: number
};

export type GetAlertsFiredParams = {
  accountIds?: number[];
  accountId?: number;
  esn?: number | string;
  deviceIdentifier?: number | string;
  createdOn?: string;
  sort?: string[];
  maxResults?: number;
  pg?: number;
  pageId?: number;
  pageSize?: number;
  api?: string;
  isAcknowledged?: boolean;
  dateRange?: DateRangeType,
  notificationType?: string;
  isSubAccount?: boolean;
  filters?: GenericObject;
  requiredMetaData?: boolean;
  includeIsAcknowledged?: boolean
};

/*
 * Asset Properties, Filters, Pagination Response and Results Types
 */
export enum AssetSearchableProperty {
  id              = 'id',
  name            = 'name',
  assetType       = 'assetType',
  status          = 'status',
  groups          = 'groups',
  account         = 'account',
  accountName     = 'account.name',
  createdOn       = 'createdOn',
  lastModifiedOn  = 'lastModifiedOn',
  licenseNumber   = 'licenseNumber',
  vin             = 'vin',
  vehicleTypeId   = 'vehicleTypeId',
  externalId      = 'externalId',
  isVerified      = 'isVerified',
  isUnpowered     = 'isUnpowered',
  avlEventTime    = 'avlEventTime',
  extensionsQuery = 'extensions.query'
};

export type AssetSort = `${SortDirection}${AssetSearchableProperty}`;

export type AssetFilter = {
  [AssetSearchableProperty.id]?: number | string;
  [AssetSearchableProperty.name]?: string;
  [AssetSearchableProperty.assetType]?: string;
  [AssetSearchableProperty.status]?: EntityStatus;
  [AssetSearchableProperty.groups]?: number | string;
  [AssetSearchableProperty.accountName]?: string;
  [AssetSearchableProperty.account]?: number | string;
  [AssetSearchableProperty.lastModifiedOn]?: Date | string;
  [AssetSearchableProperty.createdOn]?: Date | string;
  [AssetSearchableProperty.licenseNumber]?: string;
  [AssetSearchableProperty.vin]?: string;
  [AssetSearchableProperty.vehicleTypeId]?: number | string;
  [AssetSearchableProperty.externalId]?: string;
  [AssetSearchableProperty.isVerified]?: boolean | string;
  [AssetSearchableProperty.isUnpowered]?: boolean | string;
  [AssetSearchableProperty.extensionsQuery]?: string;
};

export interface LoadAssetsParams extends LoadParams {
  sortOn?: AssetSort[];
  filterOn?: AssetFilter;
  includeGroups?: boolean;
};

export type LoadAssetsResponse = {
  data: {
    response: {
      results: GenericObject[],
      totalCount: number;
      _links: Link[];
    }
  }
}
export type LoadAssetsResult = {
  assets: Asset[];
  totalCount: number;
  _links: Link[];
};

/*
 * Application Searchable Properties, Filters, Pagination Response and Results Types
 */
export enum ApplicationSearchableProperty {
  account = 'account',
  applicationKey = 'applicationKey',
  groups = 'groups',
  name = 'name',
  status = 'status'
};

/*
 * Device Sortable Properties in payload.
 */
export enum DeviceSortTypes {
  FIRMWARE_VERSION = 'extensions.firmwareVersion',
  VERSION_FIRMWARE = '-extensions.firmwareVersion',
  CONFIGURATION_VERSION = 'extensions.configVersion',
  VERSION_CONFIGURATION = '-extensions.configVersion',
  ACTIVATION_DATE = 'activationDate',
  DATE_ACTIVATION = '-activationDate',
  APP_ID = 'extensions.appId',
  ID_APP = '-extensions.appId',
  JPOD_CONFIG = 'JPOD Config',
  CONFIG_JPOD = '-JPOD Config',
  JPOD_FIRMWARE = 'JPOD Firmware',
  FIRMWARE_JPOD = '-JPOD Firmware',
  BLE3_FIRWARE = 'BLE3 Firmware',
  FIRWARE_BLE3 = '-BLE3 Firmware',
  VBU3_FIRWARE = 'VBU3 Firmware',
  FIRWARE_VBU3 = '-VBU3 Firmware',
  BSP_MODEM_FIRWARE = 'BSP Modem Firmware',
  FIRWARE_BSP_MODEM = '-BSP Modem Firmware',
  CELL_RADIO_FIRWARE = 'Cell Radio Firmware',
  FIRWARE_CELL_RADIO = '-Cell Radio Firmware'
}

export type ApplicationSort = `${SortDirection}${ApplicationSearchableProperty}`

export type ApplicationFilterQuery = {
  name?: string;
  status?: string;
  applicationKey?: string;
}

export interface LoadApplicationsParams extends LoadParams {
  pageSize?: number;
  sortOn?: ApplicationSort | ApplicationSort[];
  filterQuery?: ApplicationFilterQuery
};

export type LoadApplicationsResponse = {
  data: {
    response: {
      results: { application: Application}[],
      totalCount: number;
      _links: Link[];
    }
  }
};

export type LoadApplicationsResult = {
  application: Application[];
  totalCount: number;
  _links: Link[];
}

// Peg Label Searchable Properties, Filters, Pagination Response and Results Types
export enum PegLabelsSearchableProperty {
  lastModifiedOn = 'lastModifiedOn',
  name = 'name',
  type = 'type'
};

// Defines the types allowed to sort Peg Labels by
export type PegLabelsSort = `${SortDirection}${PegLabelsSearchableProperty}`

export type PegLabelsFilter = {
  [PegLabelsSearchableProperty.name]?: string | null;
  [PegLabelsSearchableProperty.type]?: PegLabelType | null;
};

// Defines the Peg Labels specific params for service calls.
export interface LoadPegLabelsParams extends LoadParams {
  sortOn?: PegLabelsSort | PegLabelsSort[];
  filterOn?: PegLabelsFilter;
  type?: string;
  isLoadAll?: boolean;
};

// Defines the Peg Labels response returned by Connect's service endpoint.
export type LoadPegLabelsResponse = {
  data: {
    response: {
      results: { pegLabel: PegLabel}[],
      totalCount: number;
      _links: Link[];
    }
  }
};

// Defines the Peg Labels result returned by our service enpoint.
export type LoadPegLabelsResult = {
  pegLabels: PegLabels;
  totalCount: number;
  _links: Links;
}

// PEG Behavior Searchable Properties, Filters, Pagination Response and Results Types
export enum PegBehaviorsSearchableProperty {
  lastModifiedOn = 'lastModifiedOn',
  name = 'name',
  status = 'status',
  inheritable = 'inheritable'
};

// Defines the types allowed to sort Peg Behaviors
export type PegBehaviorsSort = `${SortDirection}${PegBehaviorsSearchableProperty}`

export type PegBehaviorsFilter = {
  [PegBehaviorsSearchableProperty.name]?: string | null;
  [PegBehaviorsSearchableProperty.status]?: string | null;
};

// Defines the PEG Behavior specific params for service calls.
export interface LoadPegBehaviorsParams extends LoadParams {
  sortOn?: PegBehaviorsSort | PegBehaviorsSort[];
  filterOn?: PegBehaviorsFilter;
};

// Defines the search PEG Behavior specific params for service calls.
export interface SearchPegBehaviorParams extends LoadParams {
  accountId?: number;
  name?: string;
  pg?: number;
  pgSize?: number;
};

// Defines the PEG Behaviors response returned by Connect's service endpoint.
export type LoadPegBehaviorsResponse = {
  data: {
    response: {
      results: {pegBehavior: PegBehavior}[],
      totalCount: number;
      _links: Link[];
    }
  }
};

// Defines the PEG Behaviors result returned by our service enpoint.
export type LoadPegBehaviorsResult = {
  pegBehaviors: PegBehaviors;
  totalCount: number;
  _links: Links;
}

export interface LoadAlertsNotificationParams extends LoadParams {
  pageSize?: number;
  sortOn?: GenericObject;
  filterOn?: GenericObject;
  accountId?: number;
  api?: string;
};

export interface LoadCrashBoxxEventsParams extends LoadParams {
  searchTerms?: GenericObject;
  sort?: string;
  accountIdType?: string;
};

export type LoadAlertsNotificationResponse = {
  data: {
    response: {
      results: { alertsFired: AlertNotification}[],
      totalCount: number;
    }
  }
};

export type LoadAlertsNotificationResult = {
  items: AlertNotification[];
  totalCount: number;
}

/*
 * Notification (Alert Configuration) Properties, Filters, Pagination Response and
 * Results Types
 */
export enum NotificationSearchableProperty {
  id                    = 'id',
  name                  = 'name',
  status                = 'status',
  account               = 'account',
  groups                = 'groups',
  accountName           = 'account.name',
  createdOn             = 'createdOn',
  lastModifiedOn        = 'lastModifiedOn',
  activatedOn           = 'activatedOn',
  processForSubAccounts = 'processForSubAccounts',
  severity              = 'severity',
  alertType             = 'alertType',
};

export type NotificationSort = `${SortDirection}${NotificationSearchableProperty}`;

export type NotificationFilter = {
  [NotificationSearchableProperty.id]?: number | string;
  [NotificationSearchableProperty.name]?: string;
  [NotificationSearchableProperty.status]?: EntityStatus;
  [NotificationSearchableProperty.account]?: number | string;
  [NotificationSearchableProperty.groups]?: number | string;
  [NotificationSearchableProperty.accountName]?: string;
  [NotificationSearchableProperty.createdOn]?: Date | string;
  [NotificationSearchableProperty.lastModifiedOn]?: Date | string;
  [NotificationSearchableProperty.activatedOn]?: Date | string;
  [NotificationSearchableProperty.processForSubAccounts]?: boolean;
  [NotificationSearchableProperty.severity]?: string;
  [NotificationSearchableProperty.alertType]?: number;
};

/*
 * AlertType Searchable Properties, Filters, Pagination Response and Results Types
 */
export enum AlertTypeSearchableProperty {
  id                    = 'id',
  name                  = 'name',
  account               = 'account',
  accountName           = 'account.name',
  groups                = 'groups',
  createdOn             = 'createdOn',
  activatedOn           = 'activatedOn',
  lastModifiedOn        = 'lastModifiedOn',
  status                = 'status',
};

export type AlertTypeSort = `${SortDirection}${AlertTypeSearchableProperty}`;

export type AlertTypeFilter = {
  [AlertTypeSearchableProperty.id]?: number | string;
  [AlertTypeSearchableProperty.name]?: string;
  [AlertTypeSearchableProperty.status]?: EntityStatus;
  [AlertTypeSearchableProperty.account]?: number | string;
  [AlertTypeSearchableProperty.groups]?: number | string;
  [AlertTypeSearchableProperty.accountName]?: string;
  [AlertTypeSearchableProperty.createdOn]?: Date | string;
  [AlertTypeSearchableProperty.lastModifiedOn]?: Date | string;
  [AlertTypeSearchableProperty.activatedOn]?: Date | string;
};

export interface LoadNotificationsParams extends LoadParams {
  sortOn?: NotificationSort | NotificationSort[];
  filterOn?: NotificationFilter;
};

export type LoadNotificationsResponse = {
  data: {
    response: {
      results: {alertConfiguration: Notification}[],
      totalCount: number;
      _links: Link[];
    }
  }
}

export type LoadNotificationsResult = {
  notifications: Notification[];
  totalCount: number;
  _links: Link[];
};

export interface LoadNotificationTypeParams extends LoadParams {
  sortOn?: NotificationSort | NotificationSort[];
  filterOn?: NotificationFilter;
};

export type LoadNotificationTypeResponse = {
  data: {
    response: {
      results: {alertType: NotificationType}[],
      totalCount: number;
      _links: Link[];
    }
  }
}

export type LoadNotificationTypeResult = {
  notificationType: NotificationType[];
  totalCount: number;
  _links: Link[];
};

/*
 * AEMPConfig Searchable Properties, Filters, Pagination Response and Results Types
 */
export enum AEMPConfigSearchableProperty {
  id = 'id',
  account = 'account',
  name = 'name',
  status = 'status',
  pollingInterval = 'pollingInterval',
  nextPollTime = 'nextPollTime',
};

export type AEMPConfigSort = `${SortDirection}${AEMPConfigSearchableProperty}`;

export type AEMPConfigFilter = {
  [AEMPConfigSearchableProperty.id]?: number | string;
  [AEMPConfigSearchableProperty.account]?: number | string;
  [AEMPConfigSearchableProperty.name]?: string;
  [AEMPConfigSearchableProperty.status]?: EntityStatus;
  [AEMPConfigSearchableProperty.pollingInterval]?: number | string;
  [AEMPConfigSearchableProperty.nextPollTime]?: Date;
};

export interface LoadAEMPConfigParams extends LoadParams {
  pageSize?: number;
  sortOn?: AEMPConfigSort | AEMPConfigSort[];
  filterOn?: AEMPConfigFilter;
};

export type LoadAEMPConfigResponse = {
  data: {
    response: {
      results: { aempPollerConfigurator: AEMPConfig }[],
      totalCount: number;
      _links: Link[];
    }
  }
};

export type LoadAEMPConfigResult = {
  aempconfigs: AEMPConfig[];
  totalCount: number;
  _links: Link[];
};

/*
 * Events Searchable Properties, Filters, Pagination Response and Results Types
 */
export enum EventsSearchableProperty {
  deviceId = 'deviceId',
  deviceEsn = 'deviceEsn',
  eventTime = 'eventTime',
  msgType = 'msgType',
  eventType = 'eventType',
  messageUuid = 'messageUuid',
};

export type EventsSort = `${SortDirection}${EventsSearchableProperty}`;

export type EventsFilter = {
  [EventsSearchableProperty.deviceId]?: number | string;
  [EventsSearchableProperty.deviceEsn]?: number | string;
  [EventsSearchableProperty.eventTime]?: any;
  [EventsSearchableProperty.msgType]?: string;
  [EventsSearchableProperty.eventType]?: string;
  [EventsSearchableProperty.messageUuid]?: string;
};

export interface LoadEventsParams extends LoadParams {
  pageSize?: number;
  sortOn?: EventsSort | EventsSort[];
  filterOn?: EventsFilter;
  msgType?: string;
  eventTime?: any;
  deviceId?: number | string;
};

export type LoadEventsResponse = {
  data: {
    response: {
      results: { avlEvent: GenericObject }[],
      totalElements: number;
      _links: Link[];
    }
  }
};

export type LoadEventsResult = {
  events: GenericObject[];
  totalCount: number;
  _links: Link[];
};

/*
 * Role Searchable Properties, Filters, Pagination Response and Results Types
 */
export enum RoleSearchableProperty {
  id             = 'id',
  name           = 'name',
  status         = 'status',
  account        = 'account',
  accountName    = 'account.name',
  groups         = 'groups',
  activationDate = 'activationDate',
  application    = 'application'
};

export type RoleSort = `${SortDirection}${RoleSearchableProperty}`;

export type RoleFilter = {
  [RoleSearchableProperty.id]?: number | string;
  [RoleSearchableProperty.name]?: string;
  [RoleSearchableProperty.status]?: EntityStatus;
  [RoleSearchableProperty.account]?: number | string | number[];
  [RoleSearchableProperty.accountName]?: string;
  [RoleSearchableProperty.groups]?: string;
  [RoleSearchableProperty.activationDate]?: Date | string;
  [RoleSearchableProperty.application]?: string | string[];
};

export interface LoadRolesParams extends LoadParams {
  sortOn?: RoleSort | RoleSort[];
  filterOn?: RoleFilter;
  application?: string;
};

export type LoadRolesResponse = {
  data: {
    response: {
      results: {role: Role}[],
      totalCount: number;
      _links: Link[];
    }
  }
}
export type LoadRolesResult = {
  roles: Role[];
  totalCount: number;
  _links: Link[];
};
/*
 * Group Searchable Properties, Filters, Pagination Response and Results Types
 */
export enum GroupSearchableProperty {
  id             = 'id',
  name           = 'name',
  status         = 'status',
  account        = 'account',
  createdOn      = 'createdOn',
  lastModifiedOn = 'lastModifiedOn'
};

export type GroupSort = `${SortDirection}${GroupSearchableProperty}`;

export type GroupFilter = {
  [GroupSearchableProperty.id]?: number | string;
  [GroupSearchableProperty.name]?: string;
  [GroupSearchableProperty.status]?: EntityStatus;
  [GroupSearchableProperty.account]?: number | string | number[];
  [GroupSearchableProperty.createdOn]?: Date | string;
  [GroupSearchableProperty.lastModifiedOn]?: Date | string;
};

export interface LoadGroupsParams extends LoadParams {
  id?: number;
  pageId?: number;
  pageSize?: number;
  sortOn?: GroupSort | GroupSort[];
  filterOn?: GroupFilter;
  includeGlobalSearch?: boolean;
  apiVersion?: number;
  name?: string;
  maxResults?: number;
  excludeInheritedGroups?: boolean;
};

export type LoadGroupResponse = {
  data: {
    response: {
      results: {group: Group}[],
      totalCount: number;
      _links: Link[];
    }
  }
}

export type LoadGroupResult = {
  groups: Group[];
  totalCount: number;
  _links: Link[];
};

/*
 * Device Searchable Properties, Filters, Pagination Response and Results Types
 */
export enum DeviceSearchableProperty {
  id                  = 'id',
  name                = 'name',
  status              = 'status',
  account             = 'account',
  createdOn           = 'createdOn',
  lastModifiedOn      = 'lastModifiedOn',
  imei                = 'imei',
  mdn                 = 'mdn',
  externalId          = 'externalId',
  ipAddress           = 'ipAddress',
  shipDate            = 'shipDate',
  networkMode         = 'networkMode',
  activationDate      = 'activationDate',
  deviceConfiguration = 'deviceConfiguration',
  pegBehavior         = 'pegBehavior',
  groups              = 'groups',
  provisionStatus     = 'provisionStatus',
  serialNumber        = 'serialNumber',
  meid                = 'meid',
  type                = 'type',
  category            = 'category',
  architecture        = 'architecture',
  min                 = 'min',
  esn                 = 'esn',
  iccid               = 'iccid',
  airId               = 'airId',
  imsi                = 'imsi',
  networkCarrier      = 'networkCarrier',
  dmDevice            = 'dmDevice',
  networkCurrentMode  = 'networkCurrentMode',
  phoneNumber         = 'phoneNumber',
  signalStrength      = 'signalStrength'
};

export type DeviceSort = `${SortDirection}${DeviceSearchableProperty}`;

export type DeviceFilter = {
  [DeviceSearchableProperty.id]?: number | string;
  [DeviceSearchableProperty.name]?: string;
  [DeviceSearchableProperty.status]?: EntityStatus;
  [DeviceSearchableProperty.account]?: number | string;
  [DeviceSearchableProperty.createdOn]?: Date | string;
  [DeviceSearchableProperty.lastModifiedOn]?: Date | string;
  [DeviceSearchableProperty.imei]?: string;
  [DeviceSearchableProperty.mdn]?: string;
  [DeviceSearchableProperty.externalId]?: string;
  [DeviceSearchableProperty.ipAddress]?: string;
  [DeviceSearchableProperty.shipDate]?: string;
  [DeviceSearchableProperty.networkMode]?: string;
  [DeviceSearchableProperty.activationDate]?: string;
  [DeviceSearchableProperty.deviceConfiguration]?: number;
  [DeviceSearchableProperty.pegBehavior]?: number;
  [DeviceSearchableProperty.groups]?: number | string | string[];
  [DeviceSearchableProperty.provisionStatus]?: string;
  [DeviceSearchableProperty.serialNumber]?: string;
  [DeviceSearchableProperty.meid]?: string;
  [DeviceSearchableProperty.type]?: number | string | string[];
  [DeviceSearchableProperty.category]?: string[];
  [DeviceSearchableProperty.architecture]?: string[];
  [DeviceSearchableProperty.min]?: number;
  [DeviceSearchableProperty.esn]?: string;
  [DeviceSearchableProperty.iccid]?: string;
  [DeviceSearchableProperty.airId]?: string;
  [DeviceSearchableProperty.imsi]?: string;
  [DeviceSearchableProperty.networkCarrier]?: string;
  [DeviceSearchableProperty.dmDevice]?: boolean
  [DeviceSearchableProperty.networkCurrentMode]?: string;
  [DeviceSearchableProperty.phoneNumber]?: string;
  [DeviceSearchableProperty.signalStrength]?: string;
};

export interface LoadDevicesParams extends LoadParams {
  sortOn?: DeviceSort | DeviceSort[];
  filterOn?: DeviceFilter;
  includeGroups?: boolean;
  includeDeviceSettings?: boolean;
  includeSortParams?: boolean;
  includeParams?: boolean;
  omitExtensions?: boolean;
  lastData?: Device;
  customQuery?: any;
  paging?: SmartSearchPaging;
};

export type LoadDevicesResponse = {
  data: {
    response: {
      results: {device: Device}[],
      totalCount: number;
      _links: Link[];
    }
  }
}
export type LoadDevicesResult = {
  devices: Device[];
  totalCount: number;
  _links: Link[];
};

/*
 * JobStatus Searchable Properties, Filters, Pagination Response and Results Types
 */
export enum JobStatusSearchableProperty {
  name   = 'name',
  createdBy  = 'createdBy',
  status     = 'status',
  createdAt  = 'createdAt',
};

export type JobStatusSort = `${SortDirection}${JobStatusSearchableProperty}`;

export type JobStatusFilter = {
  [JobStatusSearchableProperty.name]?: string;
  [JobStatusSearchableProperty.createdBy]?: string;
  [JobStatusSearchableProperty.status]?: EntityStatus;
  [JobStatusSearchableProperty.createdAt]?: Date | string;
};

export interface LoadjobStatusParams extends LoadParams {
  sortOn?: JobStatusSort | JobStatusSort[];
  filterOn?: JobStatusFilter;
  includeGroups?: boolean;
};

export type LoadjobStatusResponse = {
  data: {
    response: {
      results: {task: JobStatus}[],
      count: number;
      _links: Link[];
    }
  }
};

export type SubtaskResult = {
  subtasks: Subtask[];
  cursor: string;
  errors?: SubtaskError[];
  error?: SubtaskErrors;
}

export type LoadjobStatusResult = {
  items: JobStatus[];
  count: number;
  _links: Link[];
};

export type LoadBulkEntitiesResult = {
  items: JobStatus[];
  count: number;
  cursor: string;
  _links: Link[];
};

export type TaskResult = {
  tasks: JobStatus[];
  errors: GenericObject[];
  cursor: string;
  sortOn?: string;
};

/*
 * Account Searchable Properties, Filters, Pagination Response and Results Types
 */
export enum AccountSearchableProperty {
  id                       = 'id',
  name                     = 'name',
  status                   = 'status',
  parent                   = 'parent',
  groups                   = 'groups',
  subscription             = 'subscription',
  externalRefID            = 'externalRefID',
  checkPulsDeviceOwnership = 'checkPulsDeviceOwnership',
  solomonId                = 'solomonId',
  createdOn                = 'createdOn',
  lastModifiedOn           = 'lastModifiedOn'
};

export type AccountSort = `${SortDirection}${AccountSearchableProperty}`;

export type AccountFilter = {
  [AccountSearchableProperty.id]?: number | string;
  [AccountSearchableProperty.name]?: string;
  [AccountSearchableProperty.status]?: EntityStatus;
  [AccountSearchableProperty.parent]?: number | string;
  [AccountSearchableProperty.groups]?: number | string | string[];
  [AccountSearchableProperty.subscription]?: number;
  [AccountSearchableProperty.externalRefID]?: string;
  [AccountSearchableProperty.checkPulsDeviceOwnership]?: boolean;
  [AccountSearchableProperty.solomonId]?: string;
  [AccountSearchableProperty.createdOn]?: Date | string;
  [AccountSearchableProperty.lastModifiedOn]?: Date | string;
};

export interface LoadAccountsParams extends LoadParams {
  parentAccountId: number;
  subAccountIds?: string | null;
  sortOn?: AccountSort | AccountSort[];
  filterOn?: AccountFilter;
  includeGlobalSearch?: boolean;
  groupSearchKey?: string;
};

export type LoadAccountsResponse = {
  data: {
    response: {
      results: {account: Account}[],
      totalCount: number;
      _links: Link[];
    }
  }
};

export type LoadAccountsResult = {
  accounts: Account[];
  totalCount: number;
  _links: Link[];
};

/*
 * User Searchable Properties, Filters, Pagination Response and Results Types
 */
export enum UserSearchableProperty {
  id             = 'id',
  name           = 'name',
  status         = 'status',
  account        = 'account',
  createdOn      = 'createdOn',
  lastModifiedOn = 'lastModifiedOn',
  username       = 'username'
};

export type UserSort = `${SortDirection}${UserSearchableProperty}`;

export type UserFilter = {
  [UserSearchableProperty.id]?: number | string;
  [UserSearchableProperty.name]?: string;
  [UserSearchableProperty.status]?: EntityStatus;
  [UserSearchableProperty.account]?: number | string;
  [UserSearchableProperty.createdOn]?: Date | string;
  [UserSearchableProperty.lastModifiedOn]?: Date | string;
  [UserSearchableProperty.username]?: string;
};

export interface LoadUsersParams extends LoadParams {
  sortOn?: UserSort | UserSort[];
  filterOn?: UserFilter;
  includeGlobalSearch?: boolean;
};

export type LoadUsersResponse = {
  data: {
    response: {
      results: {user: User}[],
      totalCount: number;
      _links: Link[];
    }
  }
};

export type LoadUsersResult = {
  users: User[];
  totalCount: number;
  _links: Link[];
};

/*
 * Schedule Searchable Properties, Filters, Pagination Response and Results Types
 */
export enum ScheduleSearchableProperty {
  id             = 'id',
  name           = 'name',
  status         = 'status',
  account        = 'account',
  createdOn      = 'createdOn',
  lastModifiedOn = 'lastModifiedOn',
  activatedOn    = 'activatedOn',
  tolerance      = 'tolerance',
  timeZone       = 'timeZone'
};

export type ScheduleSort = `${SortDirection}${ScheduleSearchableProperty}`;

export type ScheduleFilter = {
  [ScheduleSearchableProperty.id]?: number | string;
  [ScheduleSearchableProperty.name]?: string;
  [ScheduleSearchableProperty.status]?: EntityStatus;
  [ScheduleSearchableProperty.account]?: number | string;
  [ScheduleSearchableProperty.createdOn]?: Date | string;
  [ScheduleSearchableProperty.lastModifiedOn]?: Date | string;
  [ScheduleSearchableProperty.activatedOn]?: Date | string;
  [ScheduleSearchableProperty.tolerance]?: number;
  [ScheduleSearchableProperty.timeZone]?: string;
};

export interface LoadSchedulesParams extends LoadParams {
  sortOn?: ScheduleSort | ScheduleSort[];
  filterOn?: ScheduleFilter;
};

export type LoadSchedulesResponse = {
  data: {
    response: {
      results: {schedule: Schedule}[],
      totalCount: number;
      _links: Link[];
    }
  }
};

export type LoadSchedulesResult = {
  schedules: Schedule[];
  totalCount: number;
  _links: Link[];
};

/*
 * IOProfile Searchable Properties, Filters, Pagination Response and Results Types
 */
export enum IOProfileSearchableProperty {
  id             = 'id',
  profileName    = 'profileName',
  accountName    = 'account.name',
  account        = 'account',
  groups         = 'groups',
  createdOn      = 'createdOn',
};

export type IOProfileSort = `${SortDirection}${IOProfileSearchableProperty}`;

export type IOProfileFilter = {
  [IOProfileSearchableProperty.id]?: number | string;
  [IOProfileSearchableProperty.profileName]?: string;
  [IOProfileSearchableProperty.accountName]?: string;
  [IOProfileSearchableProperty.account]?: number | string;
  [IOProfileSearchableProperty.groups]?: number | string | string[];
  [IOProfileSearchableProperty.createdOn]?: string;
};

export interface LoadIOProfilesParams extends LoadParams {
  sortOn?: IOProfileSort | IOProfileSort[];
  filterOn?: IOProfileFilter;
};

export type LoadIOProfilesResponse = {
  data: {
    response: {
      results: {ioProfile: IOProfile}[],
      totalCount: number;
      _links: Link[];
    }
  }
};

export type LoadIOProfilesResult = {
  ioprofiles: IOProfile[];
  totalCount: number;
  _links: Link[];
};

/*
 * Operator Searchable Properties, Filters, Pagination Response and Results Types
 */
export enum OperatorSearchableProperty {
  id             = 'id',
  firstName      = 'firstName',
  lastName       = 'lastName',
  status         = 'status',
  account        = 'account',
  accountName    = 'account.name',
  association    = 'association',
  badgeNumber    = 'badgeNumber',
  asset          = 'asset',
  groups         = 'groups',
  createdOn      = 'createdOn',
  activationDate = 'activationDate',
};

export type OperatorSort = `${SortDirection}${OperatorSearchableProperty}`;

export type OperatorFilter = {
  [OperatorSearchableProperty.id]?: number | string;
  [OperatorSearchableProperty.firstName]?: string;
  [OperatorSearchableProperty.lastName]?: string;
  [OperatorSearchableProperty.status]?: EntityStatus;
  [OperatorSearchableProperty.account]?: number | string;
  [OperatorSearchableProperty.accountName]?: string;
  [OperatorSearchableProperty.association]?: string;
  [OperatorSearchableProperty.badgeNumber]?: string;
  [OperatorSearchableProperty.asset]?: number;
  [OperatorSearchableProperty.groups]?: number | string | string[];
  [OperatorSearchableProperty.createdOn]?: Date | string;
  [OperatorSearchableProperty.activationDate]?: Date | string;
};

export interface LoadOperatorsParams extends LoadParams {
  sortOn?: OperatorSort | OperatorSort[];
  filterOn?: OperatorFilter;
};

export type LoadOperatorsResponse = {
  data: {
    response: {
      results: {operator: Operator}[],
      totalCount: number;
      _links: Link[];
    }
  }
};

export type LoadOperatorsResult = {
  operators: Operator[];
  totalCount: number;
  _links: Link[];
};

/*
 * Route Searchable Properties, Filters, Pagination Response and Results Types
 */
export enum RouteSearchableProperty {
  account      = 'account',
  groups       = 'groups',
  type         = 'type',
  category     = 'category',
  accountName  = 'account.name',
  name         = 'name',
  status       = 'status'
};

export type RouteSort = `${SortDirection}${RouteSearchableProperty}`;

export type RouteFilter = {
  [RouteSearchableProperty.account]?: number | string;
  [RouteSearchableProperty.groups]?: number | string | string[];
  [RouteSearchableProperty.type]?: string;
  [RouteSearchableProperty.category]?: string;
  [RouteSearchableProperty.accountName]?: string;
  [RouteSearchableProperty.name]?: string;
  [RouteSearchableProperty.status]?: EntityStatus;
};

export interface LoadRoutesParams extends LoadParams {
  sortOn?: RouteSort | RouteSort[];
  filterOn?: RouteFilter;
};

export type LoadRoutesResponse = {
  data: {
    response: {
      results: {route: Route}[],
      totalCount: number;
      _links: Link[];
    }
  }
};

export type LoadRoutesResult = {
  routes: Route[];
  totalCount: number;
  _links: Link[];
};

/*
 * Geofence Searchable Properties, Filters, Pagination Response and Results Types
 */
export enum GeofenceSearchableProperty {
  landmarkName       = 'landmarkName',
  landmarkAccountId  = 'landmarkAccountId',
  landmarkAccount    = 'landmarkAccount',
  landmarkStatus     = 'landmarkStatus',
  geofenceType       = 'geofenceType',
  landmarkCategory   = 'landmarkCategory',
  landmarkRestricted = 'landmarkRestricted',
  createdDate        = 'createdDate',
  modifiedDate       = 'modifiedDate',
  eventTime          = 'eventTime'
};

export type GeofenceSort = `${GeofenceSearchableProperty} ${'asc' | 'desc'}`;

export type GeofenceFilter = {
  [GeofenceSearchableProperty.landmarkName]?: string;
  [GeofenceSearchableProperty.landmarkAccountId]?: number;
  [GeofenceSearchableProperty.landmarkAccount]?: string;
  [GeofenceSearchableProperty.landmarkStatus]?: string;
  [GeofenceSearchableProperty.geofenceType]?: string;
  [GeofenceSearchableProperty.landmarkCategory]?: string;
  [GeofenceSearchableProperty.landmarkRestricted]?: string;
  [GeofenceSearchableProperty.createdDate]?: string;
  [GeofenceSearchableProperty.modifiedDate]?: string;
};

export type LoadGeofencesParams = {
  accountIds: number[];
  ancestorAccountIds: number[];
  resultOffset?: number;
  resultRecordCount?: number;
  sortOn?: GeofenceSort | GeofenceSort[] | string;
  filterOn?: GeofenceFilter;
};

export type LoadGeofencesResponse = {
  data: {
    response: {
      results: {attributes: GeoFenceAttributes}[],
      totalCount: number;
      _links: Link[];
    }
  }
};

export type LoadGeofencesResult = {
  geofences: GeoFenceAttributes[];
  totalCount: number;
};

/**
 * Defines the pagination parameters for maintenance services.
 */
export interface LoadMaintenanceParams {
  accountId?: number;
  pageId?: number;
  pageSize?: number;
  sort?: MaintenanceScheduleSort[] | MaintenanceServiceItemSort[];
  filters?: MaintenanceScheduleFilter | MaintenanceServiceItemFilter;
  hierarchy?: boolean;
  inherited?: boolean;
};

/*
 * Maintenance Schedule Properties, Filters, Pagination Response and Results Types
 */
export enum MaintenanceScheduleSearchableProperty {
  name           = 'name',
  assetType      = 'assetType',
  assetCategory  = 'assetCategory'
};

export type MaintenanceScheduleSort = `${SortDirection | ''}${MaintenanceScheduleSearchableProperty}`;

export type MaintenanceScheduleFilter = {
  [MaintenanceScheduleSearchableProperty.name]?: string;
  [MaintenanceScheduleSearchableProperty.assetType]?: string;
  [MaintenanceScheduleSearchableProperty.assetCategory]?: string;
};

export interface LoadMaintenanceSchedulesParams extends LoadMaintenanceParams {
};

export type LoadMaintenanceSchedulesResponse = {
  data: {
    rows: MaintenanceSchedule[],
    count: number
  }
};

export type LoadMaintenanceSchedulesResult = {
  schedules: MaintenanceSchedule[];
  totalCount: number;
};

/*
 * Maintenance Service Item Properties, Filters, Pagination Response and Results Types
 */
export enum MaintenanceServiceItemSearchableProperty {
  name                 = 'name',
  intervalDays         = 'intervalDays',
  reminderDays         = 'reminderDays',
  intervalOdometer     = 'intervalOdometer',
  reminderOdometer     = 'reminderOdometer',
  intervalEngineHours  = 'intervalEngineHours',
  reminderEngineHours  = 'reminderEngineHours',
};

export type MaintenanceServiceItemSort = `${SortDirection | ''}${MaintenanceServiceItemSearchableProperty}`;

export type MaintenanceServiceItemFilter = {
  [MaintenanceServiceItemSearchableProperty.name]?: string;
  [MaintenanceServiceItemSearchableProperty.intervalDays]?: number;
  [MaintenanceServiceItemSearchableProperty.reminderDays]?: number;
  [MaintenanceServiceItemSearchableProperty.intervalOdometer]?: number;
  [MaintenanceServiceItemSearchableProperty.reminderOdometer]?: number;
  [MaintenanceServiceItemSearchableProperty.intervalEngineHours]?: number;
  [MaintenanceServiceItemSearchableProperty.reminderEngineHours]?: number;
};

export interface LoadMaintenanceServiceItemParams extends LoadMaintenanceParams {
  convertUnits?: boolean
};

export type LoadMaintenanceServiceItemResponse = {
  data: {
    rows: MaintenanceServiceItem[],
    count: number
  }
};

export type LoadMaintenanceServiceItemResult = {
  serviceItems: MaintenanceServiceItem[];
  totalCount: number;
};

/*
 * Device Types Properties, Pagination Response and Results Types
 */
export enum DeviceTypeSearchableProperty {
  name         = 'name',
  architecture = 'architecture',
  category     = 'category',
  version      = 'validationMetaData.fields.version'
};

export type DeviceTypesSort = `${SortDirection}${DeviceTypeSearchableProperty}`;

export type DeviceTypesFilter = {
  [DeviceTypeSearchableProperty.name]?: string;
};

export interface LoadDeviceTypesParams extends LoadParams {
  sortOn?: DeviceTypesSort[];
  filterOn?: DeviceTypesFilter;
};

export type LoadDeviceTypesResponse = {
  data: {
    response: {
      results: DeviceTypes,
      totalCount: number;
    }
  }
}
export type LoadDeviceTypesResult = {
  items: DeviceTypes;
  totalCount: number;
};

// Device Configuration Searchable Properties, Filters, Pagination Response and Results Types
export enum DeviceConfigsSearchableProperty {
  name = 'name'
};

// Defines the types allowed to sort Device Configurations by
export type DeviceConfigsSort = `${SortDirection}${DeviceConfigsSearchableProperty}`

export type DeviceConfigsFilter = {
  [DeviceConfigsSearchableProperty.name]?: string | null;
};

// Defines the Device Configurations specific params for service calls.
export interface LoadDeviceConfigsParams extends LoadParams {
  sortOn?: DeviceConfigsSort | DeviceConfigsSort[];
  filterOn?: DeviceConfigsFilter;
};

// Defines the Device Configurations response returned by Connect's service endpoint.
export type LoadDeviceConfigsResponse = {
  data: {
    response: {
      results: {deviceConfiguration: DeviceConfiguration}[],
      totalCount: number;
      _links: Link[];
    }
  }
};

// Defines the Device Configurations result returned by our service endpoint.
export type LoadDeviceConfigsResult = {
  deviceConfigurations: DeviceConfigurations;
  totalCount: number;
  _links: Links;
};

export enum DownloadJobsSearchableProperty {
  createdOn  = 'createdOn'
};

export type DownloadJobsSort = `${SortDirection}${DownloadJobsSearchableProperty}`;

export interface LoadDownloadJobsParams extends LoadParams {
  sortOn?: DownloadJobsSort[];
  filterOn?: GenericObject;
  name?: string;
};

export interface LoadDevicesStatusParams extends LoadParams {
  index?: number
}

export type LoadDownloadDevicesJobsParams = {
  items: DownloadJob[];
  count: number;
  _links: Link[];
};

export type LoadDownloadJobsResponse = {
  data: {
    response: {
      results: {job: DownloadJob}[],
      totalCount: number;
      _link: Link[];
    }
  }
}

export type LoadDownloadJobsResult = {
  items: DownloadJob[];
  totalCount: number;
  _links: Link[];
};

export interface LoadDevicesStatusResult extends LoadParams {
  items: {
    device: Device
  }[];
  totalCount: number;
};

export type DeviceFilesSort = `${SortDirection}${DeviceTypeSearchableProperty}`;

export interface LoadDeviceFilesParams extends LoadParams {
  sortOn?: DeviceFilesSort[];
  filterOn?: GenericObject;
  isResults?: boolean;
  includeOnlyThisAccount?: boolean;
  status?: EntityStatus;
  suppressNullVersions?: boolean;
};

export type LoadDeviceFilesResponse = {
  data: {
    response: {
      results: GenericObject[],
      totalCount: number;
    }
  }
}
export type LoadDeviceFilesResult = {
  items: CTCEntity[],
  totalCount: number;
};

export type LoadPlatformDeviceTypesResult = {
  items: CTCEntity[],
  totalCount: number;
};

export type LoadPlatformDeviceTypesResponse = {
  data: GenericObject[]
};

// Device Part Numbers Searchable Properties, Filters, Pagination Response and Results Types
export enum DevicePartNumbersSearchableProperty {
  devicePartNum           = 'devicePartNum',
  deviceType              = 'deviceType',
  deviceConfigurationName = 'deviceConfigurationName',
  isThirdParty            = 'isThirdParty',
  carrier                 = 'carrier'
};

// Defines the types allowed to sort by Device Part Numbers
export type DevicePartNumbersSort = `${SortDirection}${DevicePartNumbersSearchableProperty}`

export type DevicePartNumbersFilter = {
  [DevicePartNumbersSearchableProperty.devicePartNum]?: string | null;
  [DevicePartNumbersSearchableProperty.deviceType]?: string | null;
  [DevicePartNumbersSearchableProperty.deviceConfigurationName]?: string | null;
  [DevicePartNumbersSearchableProperty.isThirdParty]?: string | null;
  [DevicePartNumbersSearchableProperty.carrier]?: string | null;
};

// Defines the Device Part Numbers specific params for service calls.
export interface LoadDevicePartNumbersParams extends LoadParams {
  sortOn?: DevicePartNumbersSort | DevicePartNumbersSort[];
  filterOn?: DevicePartNumbersFilter;
};

// Application Ids Searchable Properties, Filters, Pagination Response and Results Types
export enum ApplicationIdsSearchableProperty {
  manufacturer   = 'manufacturer',
  microprocessor = 'microprocessor',
  model          = 'model',
  status         = 'status',
  cellularModem  = 'cellularModem',
  deviceFamily   = 'deviceFamily',
  carrier        = 'carrier'
};

// Defines the types allowed to sort by Application Ids
export type ApplicationIdsSort = `${SortDirection}${ApplicationIdsSearchableProperty}`

export type ApplicationIdsFilter = {
  [ApplicationIdsSearchableProperty.manufacturer]?: string | null;
  [ApplicationIdsSearchableProperty.microprocessor]?: string | null;
  [ApplicationIdsSearchableProperty.model]?: string | null;
  [ApplicationIdsSearchableProperty.status]?: string | null;
  [ApplicationIdsSearchableProperty.cellularModem]?: string | null;
  [ApplicationIdsSearchableProperty.deviceFamily]?: string | null;
  [ApplicationIdsSearchableProperty.carrier]?: string | null;
};

// Defines the Application Ids specific params for service calls.
export interface LoadApplicationIdsParams extends LoadParams {
  sortOn?: ApplicationIdsSort | ApplicationIdsSort[];
  filterOn?: ApplicationIdsFilter;
};

// Shipments Searchable Properties, Filters, Pagination Response and Results Types
export enum ShipmentsSearchableProperty {
  trackingNum = 'trackingNum',
  shipperId = 'shipperId',
  salesOrderNum = 'salesOrderNum',
  purchaseOrderNum = 'purchaseOrderNum',
  ctcRegion = 'ctcRegion',
  shipDate = 'shipDate',
  createdDate = 'createdDate',
  lastModifiedOn = 'lastModifiedOn'
};

// Defines the types allowed to sort by Shipments
export type ShipmentsSort = `${SortDirection}${ShipmentsSearchableProperty}`

export type ShipmentsFilter = {
  [ShipmentsSearchableProperty.trackingNum]?: string | null;
  [ShipmentsSearchableProperty.shipperId]?: string | null;
  [ShipmentsSearchableProperty.salesOrderNum]?: string | null;
  [ShipmentsSearchableProperty.purchaseOrderNum]?: string | null;
  [ShipmentsSearchableProperty.ctcRegion]?: string | null;
  [ShipmentsSearchableProperty.shipDate]?: string | null;
  [ShipmentsSearchableProperty.createdDate]?: string | null;
  [ShipmentsSearchableProperty.lastModifiedOn]?: string | null;
};

export type ShipmentsSearch = {
  search: {
    sort?: ShipmentsSort | ShipmentsSort[];
    searchTerms?: {
      solomonId?: string;
      accountId: number;
    }
  }
};

export type ShipmentsSearchParams = {
  accountId: number;
  solomonId?: string;
  sort?: ShipmentsSort[];
  searchParams?: BaseSearchFilters;
}

// Defines the Shipments specific params for service calls.
export interface LoadShipmentsParams extends LoadParams {
  sortOn?: ShipmentsSort | ShipmentsSort[];
  filterOn?: ShipmentsFilter;
};

// Inventory Devices Searchable Properties, Filters, Pagination Response and Results Types
export enum InventoryDevicesSearchableProperty {
  deviceId        = 'deviceId',
  deviceTypeName  = 'deviceTypeName',
  serialNumber    = 'serialNumber',
  solomonId       = 'solomonId',
  currRegion      = 'currRegion',
  shipDate        = 'shipDate',
  custScriptVer   = 'custScriptVer',
  firmwareVersion = 'firmwareVersion',
  externalData    = 'externalData',
  imei            = 'imei',
  imsi            = 'imsi',
  iccid           = 'iccid'
};

// Defines the types allowed to sort Inventory Devicess by
export type InventoryDevicesSort = `${SortDirection}${InventoryDevicesSearchableProperty}`

export type InventoryDevicesFilter = {
  [InventoryDevicesSearchableProperty.deviceId]?: string | number | null;
  [InventoryDevicesSearchableProperty.deviceTypeName]?: string | null;
  [InventoryDevicesSearchableProperty.serialNumber]?: string | null;
  [InventoryDevicesSearchableProperty.solomonId]?: string | null;
  [InventoryDevicesSearchableProperty.currRegion]?: string | null;
  [InventoryDevicesSearchableProperty.shipDate]?: string | null;
  [InventoryDevicesSearchableProperty.custScriptVer]?: string | null;
  [InventoryDevicesSearchableProperty.firmwareVersion]?: string | null;
  [InventoryDevicesSearchableProperty.externalData]?: string | null;
  [InventoryDevicesSearchableProperty.imei]?: string | null;
  [InventoryDevicesSearchableProperty.imsi]?: string | null;
  [InventoryDevicesSearchableProperty.iccid]?: string | null;
};

export type InventorySearch = {
  search: {
    sort?: InventoryDevicesSort | InventoryDevicesSort[];
    searchTerms?: {
      status: EntityStatus;
      currRegion: string;
    }
  }
};

// Defines the Inventory Devices specific params for service calls.
export interface LoadInventoryDevicesParams extends LoadParams {
  sortOn?: InventoryDevicesSort | InventoryDevicesSort[];
  filterOn?: InventorySearch;
};

// Defines the Inventory Devices response returned by Connect's service endpoint.
export type LoadInventoryDevicesResponse = {
  data: {
    response: {
      results: { deviceProvisioning: InventoryDevice}[],
      totalCount: number;
      _links: Link[];
    }
  }
};

// Defines the Inventory Devices result returned by our service enpoint.
export type LoadProvisionedDevicesResult = {
  devices: InventoryDevices;
  totalCount: number;
  _links: Links;
}

export type AuditParams = {
  id: number,
  entity: Entity,
  pageId?: number,
  pageSize?: number,
  includeEntity?: boolean,
  includeUser?: boolean,
  customFilter?: DateRangeType,
  enableDateFilter?: boolean
}

export type AuditResults = {
  results: AuditRecord[],
  links: Link[],
  totalCount: number
}

export type BulkOperationParams = {
  items: CTCEntity[] | GeoFenceAttributes[],
  api?: (item: CTCEntity | GeoFenceAttributes | CTCEntity[], ...args: unknown[]) => Promise<CTCEntity | CTCEntity[]>,
  getOptions?: (item: CTCEntity) => unknown,
  name?: (item: CTCEntity) => string | string,
  context?: UserContext | GenericObject
}

export type BulkOperationResults = {
  results: CTCEntity[],
  errors: CTCEntity[]
}

// extended error type ...
export interface ExtendedErrorType {
  error: string;
  message: string;
  name: string;
  [key: string]: any;
  code?: number;
};

// extended result type ...
export type ExtendedResults = {
  results: CTCEntity[];
  errors: CTCEntity[] | ExtendedErrorType[];
}

// defines the type definitions for jasper options ...
export interface JasperOptions extends ServerInformation {
  user?: string;
  password?: string;
};

// Generic Server Information Type
export interface ServerInformation {
  baseURL: string;
  baseReportPath: string;
  token: string;
  tokenName: string;
};

// defines the type definitions for date range ...
export interface DateRangeType {
  startDate: string;
  endDate: string;
};

// defines the type definitions for date period ...
export interface DatePeriodType extends DateRangeType {
  period: DateRange;
};

// Defines the types available for searching devices
export enum DeviceSearchTypes {
  FIRMWARE_VERSION = 'firmwareVersion',
  CONFIGURATION_VERSION = 'configVersion',
  DEVICE_MODEL = 'deviceModel',
  APP_ID = 'appId'
}

// Defines the types available for searching the activity of devices
export enum DeviceActivitySearchTypes {
  INACTIVE = 'inactiveDevices',
  ACTIVE = 'activeDevices'
}

// Enum depicting the types of available device communication searches
export enum DeviceCommunicationSearchTypes {
  LAST_7_DAYS  = 'lastSevenDays',
  LAST_14_DAYS = 'lastFourteenDays',
  LAST_30_DAYS = 'lastThirtyDays',
  LAST_90_DAYS = 'lastNinetyDays',
}
// defines the method definitions for API requests ...
export enum Method  {
  post = 'post',
  get = 'get',
  put = 'put',
  delete = 'delete',
  head = 'head'
};

/**
 * Parsed Accum types for voltage, temperature, light, battery state and
 */
export interface AccumType {
  type: 'Temperature' | 'Lumens' | 'Battery' | 'Voltage' | 'None';
  index: number;
  label: string;
  units?: string;
  value: number;
}

export interface RssiType {
  units: string | number;
  value: number;
}

export interface ScDeviceFeaturesAttributes {
  OBJECTID: number;
  account_id: number;
  account_status: string;
  account_name: string;
  device_name: string;
  asset_category: string;
  asset_id: number;
  asset_status: EntityStatus;
  asset_name: string;
  asset_type: string;
  asset_alias: string;
  assets: string;
  device_id: number;
  device_esn: string;
  device_id_type: string;
  device_id_value: string;
  device_air_id: string;
  message_type_id: number,
  message_type: string;
  message_sequence_number: number;
  message_time: DateType;
  message_received_time: DateType;
  message_uuid: string;
  created: DateType;
  operator_name?: String;
  pipeline_rx_time: DateType;
  db_write_time: DateType;
  reporting_device_id: number;
  reporting_device_status: unknown;
  reporting_device_name: unknown;
  device_type?: string;
}

export interface ScDeviceFeaturesAttributesResponse extends ScDeviceFeaturesAttributes {
  gps: string;
  location: string;
  wireless_modem: string;
  accum_battery_voltage: string;
  accum_temperature: string;
  accum_light_level: string;
  accum_battery_state: string;
  accum_battery_level: string;
  payload: string;
  landmark: string;
  rssi: string;
  sensors: string;
}

export interface ScDeviceFeaturesAttributesItems extends ScDeviceFeaturesAttributes {
  gps: {
    latitude: number;
    longitude: number;
    satellites: number;
    computedFixStatus: boolean
  };
  location: GenericObject;
  wireless_modem: GenericObject;
  accum_battery_voltage: AccumType;
  accum_temperature: AccumType;
  accum_light_level: AccumType;
  accum_battery_state: AccumType;
  accum_battery_level: AccumType;
  payload: GenericObject;
  landmark: GenericObject;
  rssi: RssiType;
}

export interface ScDeviceFeaturesResponse {
  geometry?: {
    x: number;
    y: number;
  },
  attributes: ScDeviceFeaturesAttributesResponse;
}

export interface ScDeviceFeaturesType extends ScDeviceFeaturesAttributesItems {
  id: number;
  assetName: string;
  assetType: AssetType;
  isScDevice: boolean;
  geometry: {
    x: number;
    y: number;
  }
}

/**
 * API response type for SC Devices
 */
export interface ScDevicesResponse {
  totalCount: number;
  geometryType: string;
  spatialReference: {
    wkid: number;
    latestWkid: number;
  },
  features: ScDeviceFeaturesResponse[];
}

/**
 * Response transformation type
 */
export interface ScDevicesAsAssetsItems {
  totalCount: number;
  geometryType: string;
  spatialReference: {
    wkid: number;
    latestWkid: number;
  },
  features: ScDeviceFeaturesType[];
}

export interface ScResponseType {
  status: number;
  statusText: string;
  data: ScDevicesResponse;
}

export enum Cursor {
  End = 'end'
};

export interface LoadGeofenceEventsParams extends LoadParams {
  sortOn?: GeofenceSort | GeofenceSort[];
  filterOn?: AssetFilter | string;
  returnGeometry?: boolean;
  isAccountSearch?: boolean;
  params?: QueryParams;
};

export interface AssetAndCountType {
  count: number
  assets: CVSAssetTypes
}

export interface DriverLeaderBoard {
  operator_id: number
  name: string
  group_name: string
}

export interface LoadNotificationEventsParams extends LoadParams {
  sortOn?: AssetSort[];
  filterOn?: AssetFilter;
}

// Defines the API Gateway smart search type.
export interface APIGatewaySmartSearch {
  smartSearch: {
    search: SmartSearch & {
      [searchField: string]: any;
    }
  }
}

// Defines the API Gateway member search type.
export interface APIGatewayMemberSearch {
  memberSearch: {
    search: {
      searchTerms?: {
        account?: string,
        [key: string]: string;
      }
    }
  }
}

// Defines the API Gateway search type.
export interface APIGatewaySearch {
  searchTerm: {
    search: {
      searchTerms: {
        id: string;
      }
    }
  }
}

export enum HierLevel {
  NONE = '',
  ALL = 'ALL'
};

type ServiceSearchParams = {
  hierLevel?: HierLevel,
  type?: Entity;
}

export interface SmartSearch {
  smartSearch: ServiceSearchParams & {
    queryString?: string;
    accountId?: number;
  }
};

export interface SmartSearchCriteria {
  searchCriteria: SmartSearch[]
}

export interface MemberSearch {
  memberSearch: ServiceSearchParams & {
    searchTerms?: {
      account?: string,
      [key: string]: string;
    }
  }
};

export interface MemberSearchCriteria {
  searchCriteria: MemberSearch[]
}

export type SearchCriteria = MemberSearchCriteria | SmartSearchCriteria;
export type Search = MemberSearch | SmartSearch;

export interface QueryParams {
  groups?: number[];
  geometry?: number[][];
  categories?: any[];
  assets?: any[];
  devices?: any[];
  types?: any[];
  start?: Moment;
  end?: Moment;
  geometryType?: string;
  assetId?: number;
  assetStatus?: any[];
  geoFences?: any[];
  routes?: any[];
  searchTerm?: string;
  searchByIds?: number[];
  searchtype?: string;
  ops?: string;
};

export interface ReportQueryParams extends QueryParams {
  params: QueryParams;
  filtersApplied: boolean;
};

export interface GetDTCCodesParams {
  device: Device;
  pg?: number;
  pgsize?: number;
  idType?: string;
  useDTC2Report?: boolean;
};

export interface ExportSearch {
  smartSearch: {
    search: {
      accountId: number;
      queryString: string;
      hierLevel: string;
      sort: string;
    };
  };
}

export interface JobsTaskExportSearch {
  memberSearch: {
    search: {
      searchTerms: {
        account: number;
        jobId: number;
        taskIndex: number;
      }
    };
  };
};

export interface JobConflictParams {
  accountId: number;
  devices: string;
  transactionStatus: string;
  jobIds?: string;
  searchEntireAccount?: boolean
};
